'use client'
import { useMemo, useState } from 'react'
import { _get } from 'utils/shared'
import classNames from 'classnames'
import { Grid } from '@achieve/ascend'
import { MediaImageStandard, Typography } from 'components/Contentful'
import Section, { SECTION_VARIANTS } from 'components/Section'
import { useMemoizedContentGetter } from 'utils/contentful'
import { HandwrittenText } from 'components/HandwrittenText'

function EmployeeStoriesPolaroidCollage({ content }) {
  const { config, title, body, polaroidImages, handwrittenRules } = useMemoizedContentGetter(
    content,
    ['body', 'config', 'polaroidImages', 'title', 'handwrittenRules']
  )
  const group1 = _get(config, 'jsonContent.group1', [])
  const group2 = _get(config, 'jsonContent.group2', [])
  const rule1 = _get(handwrittenRules, 'gridItemsCollection.items[0]', {})
  const rule2 = _get(handwrittenRules, 'gridItemsCollection.items[1]', {})
  const { gridItemsCollection = [] } = polaroidImages || {}

  const group1Polaroids = useMemo(() => {
    return gridItemsCollection?.items.filter((item) => group1.includes(item?.fieldName))
  }, [gridItemsCollection, group1])

  const group2Polaroids = useMemo(() => {
    return gridItemsCollection?.items.filter((item) => group2.includes(item?.fieldName))
  }, [gridItemsCollection, group2])

  return (
    <Section
      contain={false}
      variant={SECTION_VARIANTS.purpleBlueGradient1}
      showPattern
      className={'min-h-[436px] text-white overflow-hidden'}
      data-testid="EmployeeStoriesPolaroidCollage_section"
    >
      <Grid
        item
        container
        justifyContent="space-between"
        className={classNames('min-h-[inherit] w-full relative', 'flex-col lg:flex-row')}
      >
        {/* This container should only display on large designs where the polaroids are divided */}
        <Grid className={classNames('relative', 'hidden lg:block')} size={{ xs: 3 }}>
          <div
            className={classNames(
              'absolute h-[174px] w-[150px] -translate-x-2/4 left-2/4 bottom-0 lg:-translate-y-2/4 lg:-translate-x-2/4 lg:left-2/4 lg:top-2/4',
              'polaroid-origin-1'
            )}
          >
            {group1Polaroids.map((polaroid, pos) => (
              <Polaroid key={pos} polaroid={polaroid} />
            ))}
            <div
              className={classNames(
                'absolute h-[38px] w-[186px] translate-x-[-35px] -translate-y-64 lg:h-[45px] lg:w-[217px] lg:translate-x-32 lg:translate-y-[-95px]',
                '[&_div]:h-20'
              )}
            >
              <HandwrittenText alt={rule1?.mediaAltText} content={rule1?.mediaContent} />
            </div>
          </div>
        </Grid>
        <Grid
          size={{ xs: 6 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
          className="max-w-full max-lg:w-full"
        >
          <div className={'*:pb-8 *:last:pb-0 pt-8 pb-[480px] px-8 lg:pl-0 lg:pr-8 lg:py-0 '}>
            {title && (
              <Typography
                content={title.textContent}
                fontWeight="bold"
                variant="displayLg"
                data-testid="hero-section-title"
              />
            )}
            {body && (
              <Typography
                content={body.textContent}
                fontWeight="regular"
                variant="bodySm"
                data-testid="hero-section-title"
              />
            )}
          </div>
        </Grid>
        <Grid className={classNames('relative', 'block lg:hidden')} item>
          <div
            className={classNames(
              'absolute h-[174px] w-[150px] -translate-x-2/4 left-2/4 bottom-0 lg:-translate-y-2/4 lg:-translate-x-2/4 lg:left-2/4 lg:top-2/4',
              'polaroid-origin-mobile'
            )}
          >
            {/* For mobile, all polaroids will be rendered in the last div container */}
            {[...group1Polaroids, ...group2Polaroids].map((polaroid) => (
              <Polaroid key={polaroid?.sys?.id} polaroid={polaroid} />
            ))}
            <div
              className={classNames(
                'absolute h-[38px] w-[186px] translate-x-[-35px] -translate-y-64 lg:h-[45px] lg:w-[217px] lg:translate-x-32 lg:translate-y-[-95px]',
                '[&_div]:h-20'
              )}
            >
              <HandwrittenText alt={rule2?.mediaAltText} content={rule2?.mediaContent} />
            </div>
          </div>
        </Grid>
        <Grid className={classNames('relative', 'hidden lg:block')} size={{ xs: 3 }} item>
          <div
            className={classNames(
              'absolute h-[174px] w-[150px] -translate-x-2/4 left-2/4 bottom-0 lg:-translate-y-2/4 lg:-translate-x-2/4 lg:left-2/4 lg:top-2/4',
              'polaroid-origin-2'
            )}
          >
            {group2Polaroids.map((polaroid) => (
              <Polaroid key={polaroid?.sys?.id} polaroid={polaroid} />
            ))}
            <div
              className={
                'absolute h-[45px] w-[99px] translate-x-10 translate-y-[-174px] lg:-translate-x-32 lg:translate-y-[225px]'
              }
            >
              <HandwrittenText alt={rule2?.mediaAltText} content={rule2?.mediaContent} />
            </div>
          </div>
        </Grid>
      </Grid>
    </Section>
  )
}

const Polaroid = ({ polaroid }) => {
  const [loaded, setLoaded] = useState(false)
  return (
    <div
      className={classNames(
        'h-full w-full absolute transition-transform duration-[0.6s] ease-[ease-in-out] opacity-0',
        'data-[loaded=true]:opacity-100',
        'data-[loaded=true]:after:content-[""] data-[loaded=true]:after:h-[152px] data-[loaded=true]:after:absolute data-[loaded=true]:after:translate-x-[-137px] data-[loaded=true]:after:translate-y-[11px] data-[loaded=true]:after:w-[123px] data-[loaded=true]:after:z-1 data-[loaded=true]:after:opacity-0 data-[loaded=true]:after:bg-[#ebebeb] data-[loaded=true]:after:[animation-duration:7s] data-[loaded=true]:after:animate-expose-polaroid',
        'lg:[.polaroid-origin-1_&[data-loaded=true]:nth-child(1)]:rotate-[8.15deg] lg:[.polaroid-origin-1_&[data-loaded=true]:nth-child(1)]:translate-x-[-55px] lg:[.polaroid-origin-1_&[data-loaded=true]:nth-child(1)]:translate-y-[-109px]',
        'lg:[.polaroid-origin-1_&[data-loaded=true]:nth-child(2)]:rotate-[-9.2deg] lg:[.polaroid-origin-1_&[data-loaded=true]:nth-child(2)]:translate-x-[41px] lg:[.polaroid-origin-1_&[data-loaded=true]:nth-child(2)]:-translate-y-2.5',
        'lg:[.polaroid-origin-1_&[data-loaded=true]:nth-child(3)]:rotate-[-4.87deg] lg:[.polaroid-origin-1_&[data-loaded=true]:nth-child(3)]:translate-x-[-45px] lg:[.polaroid-origin-1_&[data-loaded=true]:nth-child(3)]:translate-y-[120px]',
        'lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(1)]:rotate-[-3.12deg] lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(1)]:translate-x-[-53px] lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(1)]:translate-y-[-145px]',
        'lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(2)]:rotate-[7.16deg] lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(2)]:translate-x-[101px] lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(2)]:translate-y-[-153px]',
        'lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(3)]:rotate-[1.39deg] lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(3)]:translate-x-[29px] lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(3)]:translate-y-[-57px]',
        'lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(4)]:rotate-[-8.47deg] lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(4)]:translate-x-[-104px] lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(4)]:translate-y-1',
        'lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(5)]:rotate-[-8.73deg] lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(5)]:translate-x-[116px] lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(5)]:translate-y-[77px]',
        'lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(6)]:rotate-[5.28deg] lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(6)]:translate-x-[27px] lg:[.polaroid-origin-2_&[data-loaded=true]:nth-child(6)]:translate-y-[139px]',
        '[.polaroid-origin-mobile_&[data-loaded=true]:nth-child(1)]:rotate-[8.15deg] [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(1)]:translate-x-[-180px] [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(1)]:translate-y-[-300px]',
        '[.polaroid-origin-mobile_&[data-loaded=true]:nth-child(2)]:rotate-[-9.2deg] [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(2)]:translate-x-[-100px] [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(2)]:translate-y-[-180px]',
        '[.polaroid-origin-mobile_&[data-loaded=true]:nth-child(3)]:rotate-[-4.87deg] [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(3)]:translate-x-[-152px] [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(3)]:translate-y-[-65px]',
        '[.polaroid-origin-mobile_&[data-loaded=true]:nth-child(4)]:hidden',
        '[.polaroid-origin-mobile_&[data-loaded=true]:nth-child(5)]:rotate-[7.16deg] [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(5)]:translate-x-[150px] [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(5)]:translate-y-[-280px] z-1',
        '[.polaroid-origin-mobile_&[data-loaded=true]:nth-child(6)]:rotate-[-5deg] [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(6)]:translate-x-[152px] [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(6)]:translate-y-[-110px]',
        '[.polaroid-origin-mobile_&[data-loaded=true]:nth-child(7)]:rotate-[-8.47deg] [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(7)]:-translate-x-12 [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(7)]:translate-y-[15px] z-1',
        '[.polaroid-origin-mobile_&[data-loaded=true]:nth-child(8)]:rotate-2 [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(8)]:-translate-x-1 [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(8)]:translate-y-[-99px]',
        '[.polaroid-origin-mobile_&[data-loaded=true]:nth-child(9)]:rotate-[5.28deg] [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(9)]:translate-x-[99px] [.polaroid-origin-mobile_&[data-loaded=true]:nth-child(9)]:translate-y-[-18px]'
      )}
      data-loaded={loaded}
    >
      <MediaImageStandard
        alt={polaroid?.mediaAltText}
        content={polaroid?.mediaContent}
        onLoad={() => setLoaded(true)}
        className={'w-full h-full'}
        width={125}
      />
    </div>
  )
}

EmployeeStoriesPolaroidCollage.displayName = 'EmployeeStoriesPolaroidCollage'

export { EmployeeStoriesPolaroidCollage }
export default EmployeeStoriesPolaroidCollage
